import React, { useMemo } from 'react';
import { useFormStyles } from '../styles';
import {
  CURRENCY_OPTIONS,
  getPipeOriginOptions,
  PIPE_COMPANY_OPTIONS,
  PIPE_ORIGIN_OPTIONS,
  STATUS_OPTIONS,
} from '../constants';
import { GeneralTabProps } from '../types';
import { LabeledField, TextInput, SelectInput } from '../components/FormControls';
import { SkuSearchSelect } from '../components/SkuSearchSelect';

const COMPANY_SELECT_OPTIONS = [
  { id: '', name: 'Seleccionar compañía' },
  ...PIPE_COMPANY_OPTIONS,
];

const ORIGIN_SELECT_PLACEHOLDER = { id: '', name: 'Seleccionar origen' };

export const GeneralTab = ({
  values,
  onChange,
  isEditing,
  products,
  loading,
  marketplace,
}: GeneralTabProps) => {
  const classes = useFormStyles();
  const fieldsEnabled = isEditing || Boolean(values.sku);
  const isPipeMarketplace = marketplace === 'pipestore';

  const originOptions = useMemo(() => {
    const options = getPipeOriginOptions(values.company);
    const mergedOptions = [...options];

    if (values.origin && !mergedOptions.some((option) => option.id === values.origin)) {
      const currentOrigin = PIPE_ORIGIN_OPTIONS.find((option) => option.id === values.origin);
      if (currentOrigin) mergedOptions.push(currentOrigin);
    }

    return [ORIGIN_SELECT_PLACEHOLDER, ...mergedOptions];
  }, [values.company, values.origin]);

  const handleCompanyChange = (companyId: string) => {
    onChange('company', companyId);
    if (companyId === '2' && values.origin === '3') {
      onChange('origin', '');
    }
  };

  return (
    <div className={classes.grid}>
      {isEditing ? (
        <div className={classes.fieldFull}>
          <div className={classes.grid}>
            <LabeledField label="SKU">
              <TextInput value={values.sku} disabled />
            </LabeledField>
            <LabeledField label="Pub. ID">
              <TextInput value={values.publicationId} disabled />
            </LabeledField>
          </div>
        </div>
      ) : (
        <LabeledField
          label="SKU"
          required
          fullWidth
          hint="Seleccioná el SKU para autocompletar nombre y categoría."
        >
          <SkuSearchSelect
            value={values.sku}
            products={products}
            loading={loading}
            onSelect={(option) => {
              onChange('sku', option.sku);
              onChange('productId', String(option.product_id));
              onChange('productName', option.name);
              onChange('category', option.category);
            }}
          />
        </LabeledField>
      )}

      <LabeledField label="Nombre del producto">
        <TextInput
          value={values.productName}
          disabled
          placeholder="Se completa al seleccionar SKU"
        />
      </LabeledField>

      <LabeledField label="Categoría">
        <TextInput value={values.category} disabled placeholder="—" />
      </LabeledField>

      {isPipeMarketplace && (
        <>
          <LabeledField
            label="Compañía"
            required
            hint="Solo se puede seleccionar una compañía por publicación."
          >
            <SelectInput
              value={values.company}
              onChange={handleCompanyChange}
              options={COMPANY_SELECT_OPTIONS}
              disabled={!fieldsEnabled}
            />
          </LabeledField>

          <LabeledField label="Origen" required>
            <SelectInput
              value={values.origin}
              onChange={(value) => onChange('origin', value)}
              options={originOptions}
              disabled={!fieldsEnabled || (!values.company && !values.origin)}
            />
          </LabeledField>

          <LabeledField label="Moneda" required>
            <SelectInput
              value={values.currency}
              onChange={(value) => onChange('currency', value)}
              options={CURRENCY_OPTIONS}
              disabled={!fieldsEnabled}
            />
          </LabeledField>
        </>
      )}

      <LabeledField label="Estado" required>
        <SelectInput
          value={values.status}
          onChange={(value) => onChange('status', value)}
          options={STATUS_OPTIONS}
          disabled={!fieldsEnabled}
        />
      </LabeledField>

      <LabeledField
        label="Relevancia"
        hint="Mayor número = mayor prioridad de visualización."
      >
        <TextInput
          type="number"
          value={values.relevance}
          onChange={(value) => onChange('relevance', value)}
          placeholder="0"
          disabled={!fieldsEnabled}
        />
      </LabeledField>

      <LabeledField
        label="Precio PVP"
        required
        fullWidth
        hint="Precio de venta al público base, sin descuentos de campaña."
      >
        <TextInput
          type="number"
          value={values.pvpPrice}
          onChange={(value) => onChange('pvpPrice', value)}
          placeholder="0.00"
          disabled={!fieldsEnabled}
          style={{ width: 200 }}
        />
      </LabeledField>
    </div>
  );
};
